import type { ButtonHTMLAttributes, ElementType, ReactNode } from "react";

import { cn } from "@/lib/utils";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?:
    | "primary"
    | "secondary"
    | "outline"
    | "danger"
    | "ghost"
    | "flashy-brand"
    | "brand-outline"
    | "nav";
  size?: "xs" | "sm" | "md" | "lg" | "icon-sm" | "icon-md";
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  as?: ElementType;
  [key: string]: unknown;
};

const variantStyles = {
  primary: "bg-brand-500 hover:bg-brand-600 text-surface-900",
  secondary: "bg-surface-700 hover:bg-surface-600 text-white",
  outline: "bg-transparent hover:bg-surface-800 text-white border border-surface-600",
  danger: "bg-error-600 hover:bg-error-700 text-white",
  ghost: "bg-transparent hover:bg-surface-800 text-surface-300",
  "flashy-brand":
    "bg-transparent border border-brand-500 text-brand-500 hover:bg-brand-500/10 hover:border-brand-400 hover:text-brand-400 hover:shadow-brand-glow-lg hover:scale-105 font-display uppercase tracking-wider transition-all duration-300 relative overflow-hidden group",
  "brand-outline":
    "bg-transparent border border-brand-500 text-brand-500 hover:bg-brand-500 hover:text-surface-900 font-display uppercase tracking-wider transition-all duration-200",
  nav: "bg-transparent hover:bg-transparent focus:ring-0 focus:ring-offset-0 text-surface-400 hover:text-brand-400 uppercase tracking-widest transition-all duration-200 relative group",
};

const sizeStyles = {
  xs: "h-5 px-2 text-2xs gap-1",
  sm: "h-8 px-3 text-sm gap-1.5",
  md: "h-10 px-4 text-sm gap-2",
  lg: "h-12 px-6 text-base gap-2.5",
  "icon-sm": "size-8 p-0",
  "icon-md": "size-10 p-0",
};

const iconSizes = {
  xs: "[&_svg]:size-3",
  sm: "[&_svg]:size-4",
  md: "[&_svg]:size-5",
  lg: "[&_svg]:size-5",
  "icon-sm": "[&_svg]:size-4",
  "icon-md": "[&_svg]:size-5",
};

export function Button({
  variant = "primary",
  size = "md",
  className = "",
  disabled,
  leftIcon,
  rightIcon,
  as: Component = "button",
  children,
  ...props
}: ButtonProps) {
  return (
    <Component
      type={Component === "button" ? "button" : undefined}
      className={cn(
        "inline-flex items-center justify-center font-bold rounded-sm leading-none transition-all duration-200 focus:outline-none",
        variant !== "flashy-brand" && variant !== "nav" && variant !== "ghost"
          ? "focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:ring-offset-surface-900"
          : "focus:ring-0",
        "disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer",
        variantStyles[variant],
        sizeStyles[size],
        iconSizes[size],
        className,
      )}
      disabled={disabled}
      {...props}
    >
      {variant === "flashy-brand" && (
        <span className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-400/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out" />
      )}
      {variant === "nav" && (
        <span className="absolute bottom-1 left-2 right-2 h-[2px] bg-brand-400 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
      )}
      <span
        className={cn(
          "inline-flex items-center gap-1.5",
          (variant === "flashy-brand" || variant === "nav") && "relative z-10",
        )}
      >
        {leftIcon}
        {children}
        {rightIcon}
      </span>
    </Component>
  );
}
